import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  GRID_SIZE,
  type GraphPath,
  type NodeData,
  type NodeId,
  type NodeRecord,
  type Point,
  type Vector,
  point,
  vector,
} from '@ipfabric/utils';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import { isTopologyGraphMetadata } from 'components/Graph/GraphState';
import { performUndoableAction } from 'components/Graph/commands/history/performUndoableAction';
import { updateMovedNodesEdges } from 'components/Graph/helpers/updateMovedNodesEdges';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { selectNodesByPath } from 'components/Graph/model/selectors/selectNodesByPath';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { Draft } from 'immer';
import { clearSharedView } from '../helpers/clearSharedView';

export interface NodeMovePosition {
  nodeId: NodeId;
  moveTo: Partial<Point>;
  /**
   * Send specific positions to undo. Useful when some effect (animation, drag etc)
   * previously modified the positions in a way that is not recorded in the undo stack.
   */
  undo?: Partial<Point>;
}

export type SetNodePositionsCommand = {
  positions: readonly NodeMovePosition[];
  /**
   * Record in undo stack, default true
   */
  undoable?: boolean;
  /**
   * This allows faster moving of a node without recalculating edges.
   *
   * Useful for animations or other heavy-render features. Taking care of edge visualisation
   * (e.g. hide them) is up to client code.
   *
   * Edges cannot be omitted when the command can be undone since it would create
   * inconsistent history chain.
   */
  unsafeOmitEdges?: boolean;
} & (
  | {
      undoable: false;

      unsafeOmitEdges?: boolean;
    }
  | {
      undoable?: true;
      unsafeOmitEdges?: false;
    }
);

type UndoableVariant = SetNodePositionsCommand & { undoable?: true };

function isUndoable(
  command: SetNodePositionsCommand,
): command is UndoableVariant {
  return command.undoable !== false;
}

function shouldUpdateEdges(command: SetNodePositionsCommand): boolean {
  return isUndoable(command) || !command.unsafeOmitEdges;
}

function moveNodeTo(movedNode: Draft<NodeData>, moveTo: Partial<Point>): void {
  if (moveTo.x !== undefined) {
    movedNode.position.x = moveTo.x;
  }
  if (moveTo.y !== undefined) {
    movedNode.position.y = moveTo.y;
  }
}

const getNodeMoveVector = (node: NodeData, moveTo: Partial<Point>): Vector => ({
  x: moveTo.x === undefined ? 0 : moveTo.x - node.position.x,
  y: moveTo.y === undefined ? 0 : moveTo.y - node.position.y,
});

const LAYOUT_BROKEN = 'layout-broken';

type MovedPathNodesInfo =
  /** mark paths where nodes were moved by different vectors */
  | typeof LAYOUT_BROKEN
  | {
      nodes: Set<NodeId>;
      movedBy: Vector;
    };
type MovedNodesByPath = Map<GraphPath, MovedPathNodesInfo>;

const updateMovedNodesByPath = (
  movedNodesByPath: MovedNodesByPath,
  movedNode: Draft<NodeData>,
  movedBy: Vector,
) => {
  if (movedNode.parentPath === null) {
    return;
  }
  pipe(
    movedNodesByPath,
    plainMap.insertOrUpdateMutable<GraphPath, MovedPathNodesInfo>(
      movedNode.parentPath,
      () => ({
        nodes: new Set([movedNode.id]),
        movedBy,
      }),
      (pathData) => {
        if (pathData === LAYOUT_BROKEN) {
          return LAYOUT_BROKEN;
        }
        if (vector.equals(pathData.movedBy, movedBy)) {
          pathData.nodes.add(movedNode.id);
          return pathData;
        }
        return LAYOUT_BROKEN;
      },
    ),
  );
};

/**
 * Check if parent and child positions overlap and returned shifted position
 */
const getChildNodeShiftedPosition = (
  parentPosition: Point,
  childPosition: Point,
): Point => {
  if (
    parentPosition.x === childPosition.x &&
    parentPosition.y === childPosition.y
  ) {
    return {
      x: childPosition.x + GRID_SIZE,
      y: childPosition.y,
    };
  }
  return childPosition;
};

/**
 * Moves all child nodes by same vector as moved node.
 * Works for first level children, has to be refactored
 * in case of need for nested children
 */
const extendPositionsToChildren = (
  nodes: NodeRecord,
  positions: readonly NodeMovePosition[],
): NodeMovePosition[] => {
  const extendedPositions: NodeMovePosition[] = [];
  for (const { nodeId, moveTo } of positions) {
    const movedNode = nodes[nodeId];

    if (movedNode && movedNode.children.length > 0) {
      const movedBy = getNodeMoveVector(movedNode, moveTo);
      const movedNodePosition = movedNode.position;

      for (const childNodeId of movedNode.children) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        const childNode = nodes[childNodeId]!;
        const childNodeShiftedPosition = getChildNodeShiftedPosition(
          movedNodePosition,
          childNode.position,
        );

        if (
          childNode &&
          // Filter out nodes moved with parent node
          // This ensures automatic alignment won't be interfered by also trying to move
          // realigned children node & in case of dragging movement, all nodes are dragged
          // by same vector so there is no need to extend position
          (!positions.some((position) => position.nodeId === childNodeId) ||
            // Check if node was shifted and proceed to apply shifted position
            childNodeShiftedPosition.x !== childNode.position.x)
        ) {
          extendedPositions.push({
            nodeId: childNode.id,
            moveTo: point.createOnVector(childNodeShiftedPosition, movedBy),
            undo: childNode.position,
          });
        }
      }
    }
  }

  return [...positions, ...extendedPositions];
};

export const setNodePositions: GraphCommandFactory<SetNodePositionsCommand> = (
  cmdContext,
  dispatch,
) => {
  const setPositions = (
    command: SetNodePositionsCommand,
    nodesRepositioned?: Set<GraphPath>,
  ) => {
    const nodesByPath = selectNodesByPath(cmdContext.getState());
    const movedNodesByPath: MovedNodesByPath = new Map();
    const positions = extendPositionsToChildren(
      cmdContext.getState().graph.nodes,
      command.positions,
    );
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    cmdContext.updateState((draft) => {
      const { graph } = draft;
      for (const { nodeId, moveTo } of positions) {
        const movedNode = graph.nodes[nodeId];
        if (!movedNode) {
          return;
        }
        const movedBy = getNodeMoveVector(movedNode, moveTo);
        if (!vector.isZero(movedBy)) {
          moveNodeTo(movedNode, moveTo);
          updateMovedNodesByPath(movedNodesByPath, movedNode, movedBy);
        }
      }

      if (isTopologyGraphMetadata(draft.graphMetadata)) {
        // Nodes repositioned have been passed to `setPositions` so we'll use them (intended to be used for undo action)
        // else we'll update nodesRepositioned in state to reflect moved nodes
        if (nodesRepositioned) {
          draft.graphMetadata.nodesRepositioned = nodesRepositioned;
        } else {
          pipe(
            movedNodesByPath,
            // filter out paths where all nodes didn't move over the same vector
            plainMap.filterMapToArray(([path, movedPathNodesInfo]) => {
              const totalNodesOnPath =
                nodesByPath.get(path)?.size ?? Number.POSITIVE_INFINITY;
              if (!Number.isFinite(totalNodesOnPath)) {
                // rather use infinity when the path is not found - indicate
                // that there are changes to be saved when we don't know due to inconsistent state
                stumble(`No info about nodes on path found for ${path}`);
              }
              // nodes moved over different vectors
              return movedPathNodesInfo === 'layout-broken' ||
                // not all nodes moved;
                // it is enough to compare size - we can move only nodes existing in the graph,
                // so when size matches, all of them must be present
                totalNodesOnPath > movedPathNodesInfo.nodes.size
                ? option.some(path)
                : option.none;
            }),
            array.map(
              plainSet.insertToMutable(draft.graphMetadata.nodesRepositioned),
            ),
          );
        }
      }

      if (shouldUpdateEdges(command)) {
        graph.edges = updateMovedNodesEdges(
          graph,
          positions.map(({ nodeId }) => nodeId),
        );
      }
    });
  };

  return (command) => {
    const { graph, graphMetadata } = cmdContext.getState();
    const nodesRepositioned = isTopologyGraphMetadata(graphMetadata)
      ? graphMetadata.nodesRepositioned
      : undefined;

    if (isUndoable(command)) {
      pipe(
        command.positions,
        (positions) =>
          positions.map((position) => ({
            nodeId: position.nodeId,
            moveTo: {
              x:
                position.moveTo.x !== undefined
                  ? (position.undo?.x ??
                    graph.nodes[position.nodeId]?.position.x)
                  : undefined,
              y:
                position.moveTo.y !== undefined
                  ? (position.undo?.y ??
                    graph.nodes[position.nodeId]?.position.y)
                  : undefined,
            },
          })),
        (undoPositions) =>
          dispatch(performUndoableAction, {
            undo: () =>
              setPositions(
                { ...command, positions: undoPositions },
                nodesRepositioned,
              ),
            redo: () => setPositions(command),
          }),
      );
    } else {
      setPositions(command);
    }
    cmdContext.updateState(clearSharedView);
  };
};
